import React, { ReactNode, useEffect, useState } from "react";

import { Grid, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { colors } from "@zapalacorp/tokens";
import Close from "Components/Generic/Icons/Close";
import { IconEmail } from "Components/Generic/Icons/IconEmail";
import { IconInfo } from "Components/Generic/Icons/IconInfo";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { useCustomerPortalContext } from "Context/CustomerPortalContext";
import { useCustomerPortalJobContext } from "Context/CustomerPortalJobContext";
import useYupValidation, { useHandleValidate, useYupSchemas } from "Hooks/useYupValidation";
import { OrderToCustomerPortal, UserCustomerPortal } from "Models/customer.portal";
import { useTranslation } from "react-i18next";
import { RequestEscalationNotify } from "Util/enums";
import * as yup from "yup";

import { ContactHistory } from "./ContactHistory";
import {
  Body,
  CancelButton,
  ContainerIcon,
  ErrorContainer,
  ErrorIcon,
  FieldLabel,
  LeftHeaderBox,
  LoadingSpinner,
  MFormControl,
  Modal,
  ModalActions,
  ModalBody,
  ModalCard,
  ModalFooter,
  ModalHeader,
  ModalHeaderContainer,
  ModalTitle,
  NotifyContact,
  NotifyContainer,
  NotifyInfo,
  NotifyInfoFields,
  OptionName,
  OptionTitle,
  PlaceholderText,
  Required,
  SelectField,
  SelectIcon,
  SelectOption,
  SendButton,
  TextAreaField,
} from "./styles";

interface Props {
  open?: boolean;
  onClose?: () => void;
}

const RequestEscalationModal = (props: Props) => {
  const { open = false, onClose } = props;

  const theme = useTheme();
  const { onRequestEscalation, isRequestingEscalation } = useCustomerPortalContext();
  const { order, setOrder } = useCustomerPortalJobContext();

  const { t: translate } = useTranslation();

  const [message, setMessage] = useState<string>("");
  const [notify, setNotify] = useState<string>("");

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const { stringRequired } = useYupSchemas(translate);

  useEffect(() => {
    if (isSubmitted) handleValidate();
  }, [message, notify]);

  const schema = yup.object().shape({
    message: stringRequired,
    notify: stringRequired,
  });

  const handleValidate = () => {
    const validated = useHandleValidate(
      {
        message,
        notify,
      },
      schema,
      setErrors
    );

    return validated;
  };

  const getUserIdByNotify = () => {
    switch (notify) {
      case RequestEscalationNotify.MOVE_COORDINATOR:
        return order?.moveCoordinator?.id;
      case RequestEscalationNotify.CONSULTANT:
        return order?.consultant?.id;
      case RequestEscalationNotify.MANAGER:
        return order?.manager?.id;
      default:
        return undefined;
    }
  };

  const getNotify = (): string => {
    switch (notify) {
      case RequestEscalationNotify.MOVE_COORDINATOR:
        return translate("moveCoordinator");
      case RequestEscalationNotify.CONSULTANT:
        return translate("consultant");
      case RequestEscalationNotify.MANAGER:
        return translate("manager");
      default:
        return "";
    }
  };

  const onSave = () => {
    setIsSubmitted(true);
    if (handleValidate()) {
      if (order?.id) {
        onRequestEscalation(
          {
            jobId: order?.id,
            userId: getUserIdByNotify(),
            comment: message,
            toWhom: getNotify(),
          },
          () => {
            NotificationToast.success(
              translate("messageSentThankYouForContactingUsWellGetBackToYouSoon")
            );
            setOrder((prev) => {
              return {
                ...(prev || {}),
                requestEscalations: [
                  ...(prev?.requestEscalations || []),
                  {
                    jobId: order?.id,
                    userId: getUserIdByNotify(),
                    comment: message,
                    toWhom: getNotify(),
                    createdAt: new Date(),
                  },
                ],
              } as OrderToCustomerPortal;
            });
            if (onClose) onClose();
          },
          () => {
            NotificationToast.error(`${translate("ohNoSomethingWentWrongPleaseTryAgain")}.`);
          }
        );
      }
    }
  };

  const getNotifyValue = (notify?: string): ReactNode | undefined => {
    switch (notify) {
      case RequestEscalationNotify.CONSULTANT:
        return (
          <span>
            {translate("consultant")} | {order?.consultant?.fullName || ""}
          </span>
        );
      case RequestEscalationNotify.MANAGER:
        return (
          <span>
            {translate("manager")} | {order?.manager?.fullName || ""}
          </span>
        );
      case RequestEscalationNotify.MOVE_COORDINATOR:
        return (
          <span>
            {translate("moveCoordinator")} | {order?.moveCoordinator?.fullName || ""}
          </span>
        );
      default:
        return <PlaceholderText>{translate("selectWhoToNotify")}</PlaceholderText>;
    }
  };

  const renderNotifyInformation = (notify?: string) => {
    if (!notify) return null;

    let notifyContact: UserCustomerPortal | undefined;

    switch (notify) {
      case RequestEscalationNotify.CONSULTANT:
        notifyContact = order?.consultant;
        break;
      case RequestEscalationNotify.MANAGER:
        notifyContact = order?.manager;
        break;
      case RequestEscalationNotify.MOVE_COORDINATOR:
        notifyContact = order?.moveCoordinator;
        break;
      default:
        notifyContact = undefined;
    }

    return (
      <NotifyInfoFields item xs={12} md={6} lg={6}>
        <NotifyContainer>
          {translate("youAreNotifying")}: {notifyContact?.fullName || ""}
          <NotifyInfo>
            <NotifyContact>
              <IconEmail height={16} width={16} color={colors.gray500} />
              <span>{notifyContact?.email || ""}</span>
            </NotifyContact>
          </NotifyInfo>
        </NotifyContainer>
      </NotifyInfoFields>
    );
  };

  return (
    <Modal open={open} onClose={isRequestingEscalation ? undefined : () => onClose && onClose()}>
      <ModalCard>
        <ModalBody>
          <ModalHeader>
            <ModalHeaderContainer>
              <LeftHeaderBox>
                <ContainerIcon>
                  <IconInfo width={20} height={20} color={theme.palette.primary.main} />
                </ContainerIcon>
                <ModalTitle>{translate("contactUs")}</ModalTitle>
              </LeftHeaderBox>
              {onClose && (
                <IconButton onClick={onClose}>
                  <Close width={24} height={24} color={colors.black} />
                </IconButton>
              )}
            </ModalHeaderContainer>
          </ModalHeader>

          <Body>
            <Grid container spacing={2} direction="row">
              <Grid item xs={12} md={6} lg={6}>
                <MFormControl error={fieldHasError("notify")} disabled={isRequestingEscalation}>
                  <FieldLabel>
                    {translate("notify")}
                    <Required>{"*"}</Required>
                  </FieldLabel>

                  <SelectField
                    disabled={isRequestingEscalation}
                    fullWidth
                    displayEmpty
                    IconComponent={SelectIcon}
                    value={notify}
                    renderValue={(value) => getNotifyValue(String(value))}
                    onChange={(event) => {
                      setNotify(String(event.target.value));
                    }}
                  >
                    {order?.moveCoordinator?.id && (
                      <SelectOption
                        key={RequestEscalationNotify.MOVE_COORDINATOR}
                        value={RequestEscalationNotify.MOVE_COORDINATOR}
                      >
                        <OptionTitle>{translate("moveCoordinator")}</OptionTitle>
                        <OptionName>{order?.moveCoordinator?.fullName || ""}</OptionName>
                      </SelectOption>
                    )}

                    {order?.consultant?.id && (
                      <SelectOption
                        key={RequestEscalationNotify.CONSULTANT}
                        value={RequestEscalationNotify.CONSULTANT}
                      >
                        <OptionTitle>{translate("consultant")}</OptionTitle>
                        <OptionName>{order?.consultant?.fullName || ""}</OptionName>
                      </SelectOption>
                    )}

                    {order?.manager?.id && (
                      <SelectOption
                        key={RequestEscalationNotify.MANAGER}
                        value={RequestEscalationNotify.MANAGER}
                      >
                        <OptionTitle>{translate("manager")}</OptionTitle>
                        <OptionName>{order?.manager?.fullName || ""}</OptionName>
                      </SelectOption>
                    )}
                  </SelectField>
                  {fieldHasError("notify") && (
                    <ErrorContainer>
                      <ErrorIcon fontSize="inherit" />
                      {getErrorByField("notify")}
                    </ErrorContainer>
                  )}
                </MFormControl>
              </Grid>

              {renderNotifyInformation(notify)}

              <Grid item xs={12}>
                <MFormControl error={fieldHasError("message")} disabled={isRequestingEscalation}>
                  <FieldLabel>
                    {translate("message")}
                    <Required>{"*"}</Required>
                  </FieldLabel>

                  <TextAreaField
                    disabled={isRequestingEscalation}
                    multiline
                    minRows={3}
                    maxRows={5}
                    inputProps={{ maxLength: 250 }}
                    placeholder={translate("writeYourMessageHere")}
                    value={message}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setMessage(event?.target.value);
                    }}
                  />
                  {fieldHasError("message") && (
                    <ErrorContainer>
                      <ErrorIcon fontSize="inherit" />
                      {getErrorByField("message")}
                    </ErrorContainer>
                  )}
                </MFormControl>
              </Grid>
            </Grid>
          </Body>

          <ContactHistory order={order} />

          <ModalFooter>
            <ModalActions>
              <CancelButton onClick={onClose} disabled={isRequestingEscalation}>
                {translate("cancel")}
              </CancelButton>
              <SendButton onClick={onSave} disabled={isRequestingEscalation}>
                {isRequestingEscalation ? <LoadingSpinner size={20} /> : translate("send")}
              </SendButton>
            </ModalActions>
          </ModalFooter>
        </ModalBody>
      </ModalCard>
    </Modal>
  );
};

export default RequestEscalationModal;
